/**
 * Dashboard Page
 *
 * Overview of key accounting metrics and quick actions.
 */

import { PageHeaderWithBreadcrumbs } from '@elsapiens/layout'
import { usePageHeader } from '@elsapiens/providers'
import { Button } from '@elsapiens/ui'
import { Link } from 'react-router-dom'

import { CashFlowChart } from './CashFlowChart'
import {
  DASHBOARD_FEATURES,
  DASHBOARD_STEPS,
  DASHBOARD_WHAT_IS_IT,
} from './dashboard-helpers'
import { FinancialMetricsCards } from './FinancialMetricsCards'
import { FiscalPeriodsWarning } from './FiscalPeriodsWarning'
import { QuickActions } from './QuickActions'
import { QuickStatsCards } from './QuickStatsCards'
import { RecentEntriesTable } from './RecentEntriesTable'
import { useDashboard } from './useDashboard'
import { DashboardIllustration } from '../../components/illustrations/AccountingIllustrations'
import { CollapsibleListInfo } from '../../components/ui'

function DashboardHeader({ description }: { description: string }): JSX.Element {
  return (
    <PageHeaderWithBreadcrumbs
      description={description}
      linkComponent={Link}
      title="Dashboard"
      variant="card"
    >
      <Link to="/journal/new">
        <Button>
          <svg className="w-4 h-4 el-mr-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
          New Journal Entry
        </Button>
      </Link>
    </PageHeaderWithBreadcrumbs>
  )
}

export function DashboardPage(): JSX.Element {
  const {
    isLoading, recentEntries, currentPeriod, trialBalance,
    hasFiscalPeriods, accountCount, financialMetrics, currency,
  } = useDashboard()
  const description = `Welcome to elBooks${currentPeriod ? ` • Current Period: ${currentPeriod.name}` : ''}`

  usePageHeader({
    title: 'Dashboard',
    description,
    breadcrumbs: [
      { label: 'Home', href: '/', as: Link },
      { label: 'Dashboard' },
    ],
  })

  return (
    <div className="flex flex-col min-w-0">
      <DashboardHeader description={description} />
      <div className="el-p-lg">
        {!isLoading && hasFiscalPeriods === false && <FiscalPeriodsWarning />}
        <QuickStatsCards
          accountCount={accountCount}
          currentPeriod={currentPeriod}
          hasFiscalPeriods={hasFiscalPeriods}
          isLoading={isLoading}
          recentEntriesCount={recentEntries.length}
          trialBalance={trialBalance}
        />
        <FinancialMetricsCards currency={currency} isLoading={isLoading} metrics={financialMetrics} />
        <CashFlowChart />
        <div className="grid grid-cols-1 lg:grid-cols-3 el-gap-lg">
          <QuickActions />
          <RecentEntriesTable entries={recentEntries} isLoading={isLoading} />
        </div>
        <CollapsibleListInfo
          description="Your central hub for monitoring financial health and accessing key accounting functions."
          features={DASHBOARD_FEATURES}
          gettingStarted={DASHBOARD_STEPS}
          illustration={<DashboardIllustration size="lg" />}
          title="About the Dashboard"
          whatIsIt={DASHBOARD_WHAT_IS_IT}
        />
      </div>
    </div>
  )
}
